import { useState, useEffect, useRef, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_RECENT = 20;

/**
 * useCallStream — connects to SSE endpoint for real-time call events.
 * Scoped to a specific locationId: only receives events for that location.
 * Reconnects automatically when locationId changes.
 *
 * @param {string|null} locationId  — current location ID to scope events
 * @param {function}    onCallEvent — callback fired for each incoming event
 * @returns {{ connected, liveCalls, liveCount, recentCalls, clearCalls }}
 */
export function useCallStream(locationId, onCallEvent) {
  const [connected, setConnected] = useState(false);
  const [liveCalls, setLiveCalls] = useState([]);
  const [recentCalls, setRecentCalls] = useState([]);
  const esRef = useRef(null);
  const onCallRef = useRef(onCallEvent);
  const processedEventsRef = useRef(new Set());

  useEffect(() => { onCallRef.current = onCallEvent; }, [onCallEvent]);

  // Reset live calls when location changes
  useEffect(() => {
    setLiveCalls([]);
    setRecentCalls([]);
  }, [locationId]);

  const fetchLiveCalls = useCallback(async () => {
    if (!locationId) return;
    try {
      const token = localStorage.getItem('auth_token');
      const url = `${BACKEND_URL}/api/webhooks/live-calls?location_id=${encodeURIComponent(locationId)}`;
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const data = await res.json();
        const calls = data.live_calls || [];
        // Filter out stale calls (safety net — primary cleanup is via call-completed webhook)
        const now = Date.now();
        const fresh = calls.filter(c => {
          const started = c.started_at_iso || c.started_at;
          if (!started) return true;
          try { return (now - new Date(started).getTime()) < 1800000; } catch { return true; } // 30 min max
        });
        setLiveCalls(fresh);
        window.dispatchEvent(new CustomEvent('live_calls_update', { detail: fresh }));
      }
    } catch { /* ignore */ }
  }, [locationId]);

  const connect = useCallback(() => {
    if (esRef.current) { esRef.current.close(); esRef.current = null; }
    setConnected(false);

    const token = localStorage.getItem('auth_token');
    if (!token || !locationId) return;

    const params = new URLSearchParams({ token, location_id: locationId });
    const url = `${BACKEND_URL}/api/events/calls?${params}`;
    const es = new EventSource(url);
    esRef.current = es;

    es.onopen = () => {
      setConnected(true);
      fetchLiveCalls();
    };

    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);

        if (data.type === 'connected') {
          setConnected(true);
          return;
        }

        if (data.type === 'call_inprogress') {
          // Deduplicate: skip if we already processed this call_id
          const dedupeKey = `${data.type}_${data.call_id}`;
          if (processedEventsRef.current.has(dedupeKey)) return;
          processedEventsRef.current.add(dedupeKey);
          // Clean old entries after 60s
          setTimeout(() => processedEventsRef.current.delete(dedupeKey), 60000);

          setLiveCalls(prev => {
            const exists = prev.some(c => c.call_id === data.call_id);
            const updated = exists ? prev : [data, ...prev];
            window.dispatchEvent(new CustomEvent('live_calls_update', { detail: updated }));
            return updated;
          });
          onCallRef.current?.(data);
        }

        if (data.type === 'call_completed') {
          const dedupeKey = `${data.type}_${data.call_id}`;
          if (processedEventsRef.current.has(dedupeKey)) return;
          processedEventsRef.current.add(dedupeKey);
          setTimeout(() => processedEventsRef.current.delete(dedupeKey), 60000);

          setRecentCalls(prev => [data, ...prev].slice(0, MAX_RECENT));
          // A completed call is by definition no longer live. Remove it
          // from liveCalls here too so we don't depend on the separate
          // `call_ended` event arriving — SSE proxies sometimes drop it,
          // and the "Incoming call in progress" row would then linger on
          // Call Tracker until the next page reload.
          setLiveCalls(prev => {
            const updated = prev.filter(c => c.call_id !== data.call_id);
            if (updated.length !== prev.length) {
              window.dispatchEvent(new CustomEvent('live_calls_update', { detail: updated }));
            }
            return updated;
          });
          onCallRef.current?.(data);
          // Notify call list to auto-reload (fixes outbound direction not showing)
          window.dispatchEvent(new CustomEvent('call_list_reload'));
        }

        if (data.type === 'call_ended') {
          setLiveCalls(prev => {
            const updated = prev.filter(c => c.call_id !== data.call_id);
            window.dispatchEvent(new CustomEvent('live_calls_update', { detail: updated }));
            return updated;
          });
        }
      } catch { /* ignore parse errors */ }
    };

    es.onerror = () => {
      setConnected(false);
      es.close();
      esRef.current = null;
      setTimeout(() => connect(), 5000);
    };
  }, [locationId, fetchLiveCalls]);

  // Connect/reconnect when locationId changes
  useEffect(() => {
    connect();
    return () => {
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
    };
  }, [connect]);

  // Periodic sync fallback — runs ALWAYS, not just when SSE is
  // disconnected. Reason: SSE can be "connected" while intermediate
  // proxies (Ingress / CDN / LB) silently drop individual events —
  // especially when 2 calls fire in quick succession. A cheap 15-s
  // poll against /api/webhooks/live-calls catches any dropped event
  // within one heartbeat and keeps the overlay + Call Tracker in sync.
  useEffect(() => {
    if (!locationId) return;
    fetchLiveCalls(); // Fetch immediately on mount / location change
    // Poll every 15s regardless of SSE state. When SSE is healthy, the
    // poll is a no-op (idempotent). When SSE misses, this heals it.
    const poll = setInterval(() => fetchLiveCalls(), 15000);
    return () => clearInterval(poll);
  }, [locationId, fetchLiveCalls]);

  const clearCalls = useCallback(() => setRecentCalls([]), []);

  return {
    connected,
    liveCalls,
    liveCount: liveCalls.length,
    recentCalls,
    clearCalls,
  };
}
